import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
    Plus, Trash2, Zap, Shield, Info, ChevronDown, Check,
    FileText, Settings2, ArrowRight, ArrowLeft, Sparkles,
    TrendingUp, AlertTriangle
} from 'lucide-react';
import { createSyncTemplate } from '@/services/api';
import type { ScaleThreshold, GuardCondition } from '@/types/price-rules';

interface CreateTemplateModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface TemplateFormData {
    name: string;
    description: string;
    sync_mode: 'automatic' | 'manual';
    scale_thresholds: ScaleThreshold[];
    guard_conditions: GuardCondition[];
}

const PRESET_TEMPLATES = {
    conservador: {
        name: 'Conservador',
        description: 'Ajustes pequenos e seguros. Ideal para começar.',
        color: 'emerald',
        icon: '🛡️',
        scale_thresholds: [
            { difference_percentage: 5, scale_percentage: 2 },
            { difference_percentage: 10, scale_percentage: 3 },
            { difference_percentage: 25, scale_percentage: 7 },
        ],
        guard_conditions: [
            { metric: 'impressions', operator: '>=', value: 500 },
        ],
    },
    moderado: {
        name: 'Moderado',
        description: 'Equilíbrio entre segurança e otimização.',
        color: 'blue',
        icon: '⚖️',
        scale_thresholds: [
            { difference_percentage: 5, scale_percentage: 3 },
            { difference_percentage: 10, scale_percentage: 5 },
            { difference_percentage: 25, scale_percentage: 12 },
        ],
        guard_conditions: [
            { metric: 'impressions', operator: '>=', value: 300 },
        ],
    },
    agressivo: {
        name: 'Agressivo',
        description: 'Ajustes maiores para otimização rápida.',
        color: 'red',
        icon: '🚀',
        scale_thresholds: [
            { difference_percentage: 5, scale_percentage: 5 },
            { difference_percentage: 10, scale_percentage: 10 },
            { difference_percentage: 25, scale_percentage: 20 },
        ],
        guard_conditions: [
            { metric: 'impressions', operator: '>=', value: 100 },
        ],
    },
};

const METRICS_OPTIONS = [
    { value: 'impressions', label: 'Impressões', icon: '👁️' },
    { value: 'pmr', label: 'PMR (%)', icon: '📊' },
    { value: 'ecpm', label: 'eCPM', icon: '💰' },
    { value: 'revenue', label: 'Revenue', icon: '💵' },
    { value: 'fill_rate', label: 'Fill Rate (%)', icon: '📈' },
];

const OPERATORS = [
    { value: '>=', label: '≥ maior ou igual' },
    { value: '>', label: '> maior que' },
    { value: '<=', label: '≤ menor ou igual' },
    { value: '<', label: '< menor que' },
    { value: '==', label: '= igual' },
    { value: '!=', label: '≠ diferente' },
];

export default function CreateTemplateModal({ open, onOpenChange }: CreateTemplateModalProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [currentStep, setCurrentStep] = useState(1);
    const [syncModeOpen, setSyncModeOpen] = useState(false);
    const [metricOpenIndex, setMetricOpenIndex] = useState<number | null>(null);
    const [operatorOpenIndex, setOperatorOpenIndex] = useState<number | null>(null);

    const [formData, setFormData] = useState<TemplateFormData>({
        name: '',
        description: '',
        sync_mode: 'automatic',
        scale_thresholds: [
            { difference_percentage: 5, scale_percentage: 3 },
            { difference_percentage: 10, scale_percentage: 5 },
            { difference_percentage: 25, scale_percentage: 10 },
        ],
        guard_conditions: [
            { metric: 'impressions', operator: '>=', value: 300 },
        ],
    });

    const createMutation = useMutation({
        mutationFn: () => createSyncTemplate(formData),
        onSuccess: () => {
            toast({ title: 'Template criado!', description: 'O template foi criado com sucesso.' });
            queryClient.invalidateQueries({ queryKey: ['sync-templates'] });
            onOpenChange(false);
            resetForm();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar template',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            sync_mode: 'automatic',
            scale_thresholds: [
                { difference_percentage: 5, scale_percentage: 3 },
                { difference_percentage: 10, scale_percentage: 5 },
                { difference_percentage: 25, scale_percentage: 10 },
            ],
            guard_conditions: [
                { metric: 'impressions', operator: '>=', value: 300 },
            ],
        });
        setCurrentStep(1);
    };

    const applyPreset = (presetKey: keyof typeof PRESET_TEMPLATES) => {
        const preset = PRESET_TEMPLATES[presetKey];
        setFormData({
            ...formData,
            name: preset.name,
            description: preset.description,
            scale_thresholds: [...preset.scale_thresholds],
            guard_conditions: [...preset.guard_conditions],
        });
    };

    const addThreshold = () => {
        setFormData({
            ...formData,
            scale_thresholds: [...formData.scale_thresholds, { difference_percentage: 0, scale_percentage: 0 }],
        });
    };

    const updateThreshold = (index: number, field: keyof ScaleThreshold, value: number) => {
        const updated = [...formData.scale_thresholds];
        updated[index] = { ...updated[index], [field]: value };
        setFormData({ ...formData, scale_thresholds: updated });
    };

    const removeThreshold = (index: number) => {
        setFormData({ ...formData, scale_thresholds: formData.scale_thresholds.filter((_, i) => i !== index) });
    };

    const addGuard = () => {
        setFormData({
            ...formData,
            guard_conditions: [...formData.guard_conditions, { metric: 'impressions', operator: '>=', value: 0 }],
        });
    };

    const updateGuard = (index: number, field: keyof GuardCondition, value: any) => {
        const updated = [...formData.guard_conditions];
        updated[index] = { ...updated[index], [field]: value };
        setFormData({ ...formData, guard_conditions: updated });
    };

    const removeGuard = (index: number) => {
        setFormData({ ...formData, guard_conditions: formData.guard_conditions.filter((_, i) => i !== index) });
    };

    const closeAllDropdowns = () => {
        setSyncModeOpen(false);
        setMetricOpenIndex(null);
        setOperatorOpenIndex(null);
    };

    const canProceed = () => {
        switch (currentStep) {
            case 1: return formData.name.trim().length >= 3;
            case 2: return formData.scale_thresholds.length > 0;
            case 3: return true;
            default: return false;
        }
    };

    const steps = [
        { number: 1, title: 'Informações', icon: FileText },
        { number: 2, title: 'Escalonamento', icon: TrendingUp },
        { number: 3, title: 'Proteções', icon: Shield },
    ];

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
            <DialogContent className="max-w-2xl p-0 overflow-hidden">
                {/* Header com Steps */}
                <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900">Criar Template de Sync</h2>
                    <p className="text-sm text-gray-500 mt-1">Configure como as regras serão sincronizadas</p>

                    {/* Steps Indicator */}
                    <div className="flex items-center justify-between mt-6">
                        {steps.map((step, index) => (
                            <div key={step.number} className="flex items-center">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                                        currentStep === step.number
                                            ? 'bg-blue-600 text-white'
                                            : currentStep > step.number
                                                ? 'bg-emerald-100 text-emerald-600'
                                                : 'bg-gray-100 text-gray-400'
                                    }`}>
                                        {currentStep > step.number ? (
                                            <Check className="h-5 w-5" />
                                        ) : (
                                            <step.icon className="h-5 w-5" />
                                        )}
                                    </div>
                                    <div className="hidden sm:block">
                                        <p className={`text-sm font-medium ${currentStep === step.number ? 'text-gray-900' : 'text-gray-500'}`}>
                                            {step.title}
                                        </p>
                                    </div>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className={`w-12 h-0.5 mx-4 ${currentStep > step.number ? 'bg-emerald-300' : 'bg-gray-200'}`} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
                    {/* Step 1: Informações Básicas */}
                    {currentStep === 1 && (
                        <div className="space-y-6">
                            {/* Presets */}
                            <div className="space-y-3">
                                <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <Sparkles className="h-4 w-4 text-amber-500" />
                                    Começar com um template pré-definido
                                </Label>
                                <div className="grid grid-cols-3 gap-3">
                                    {Object.entries(PRESET_TEMPLATES).map(([key, preset]) => (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => applyPreset(key as keyof typeof PRESET_TEMPLATES)}
                                            className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                                                formData.name === preset.name
                                                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                            }`}
                                        >
                                            <span className="text-2xl">{preset.icon}</span>
                                            <p className="font-semibold text-gray-900 mt-2">{preset.name}</p>
                                            <p className="text-xs text-gray-500 mt-1">{preset.description}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="relative flex items-center">
                                <div className="flex-1 border-t border-gray-200" />
                                <span className="px-3 text-xs text-gray-400 bg-white">ou personalize</span>
                                <div className="flex-1 border-t border-gray-200" />
                            </div>

                            {/* Nome e Modo */}
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium text-gray-700">
                                        Nome do Template <span className="text-red-500">*</span>
                                    </Label>
                                    <Input
                                        placeholder="Ex: Conservador Brasil"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="h-11 border-2 border-gray-200 rounded-xl focus:border-blue-500"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-sm font-medium text-gray-700">Modo de Sync</Label>
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => { closeAllDropdowns(); setSyncModeOpen(!syncModeOpen); }}
                                            className="w-full h-11 px-4 text-left text-sm font-medium border-2 border-gray-200 rounded-xl bg-white flex items-center justify-between hover:border-gray-300 focus:outline-none focus:border-blue-500"
                                        >
                                            <span className="flex items-center gap-2">
                                                {formData.sync_mode === 'automatic' ? (
                                                    <><Zap className="h-4 w-4 text-blue-500" /> Automático</>
                                                ) : (
                                                    <><Settings2 className="h-4 w-4 text-gray-500" /> Manual</>
                                                )}
                                            </span>
                                            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${syncModeOpen ? 'rotate-180' : ''}`} />
                                        </button>
                                        {syncModeOpen && (
                                            <>
                                                <div className="fixed inset-0 z-10" onClick={() => setSyncModeOpen(false)} />
                                                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                                    <button
                                                        type="button"
                                                        onClick={() => { setFormData({ ...formData, sync_mode: 'automatic' }); setSyncModeOpen(false); }}
                                                        className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${formData.sync_mode === 'automatic' ? 'bg-blue-50' : ''}`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <Zap className="h-4 w-4 text-blue-500" />
                                                            <div>
                                                                <p className={formData.sync_mode === 'automatic' ? 'font-medium text-blue-700' : ''}>Automático</p>
                                                                <p className="text-xs text-gray-500">Sincroniza automaticamente</p>
                                                            </div>
                                                        </div>
                                                        {formData.sync_mode === 'automatic' && <Check className="h-4 w-4 text-blue-600" />}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setFormData({ ...formData, sync_mode: 'manual' }); setSyncModeOpen(false); }}
                                                        className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${formData.sync_mode === 'manual' ? 'bg-blue-50' : ''}`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <Settings2 className="h-4 w-4 text-gray-500" />
                                                            <div>
                                                                <p className={formData.sync_mode === 'manual' ? 'font-medium text-blue-700' : ''}>Manual</p>
                                                                <p className="text-xs text-gray-500">Requer ação manual</p>
                                                            </div>
                                                        </div>
                                                        {formData.sync_mode === 'manual' && <Check className="h-4 w-4 text-blue-600" />}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Descrição */}
                            <div className="space-y-2">
                                <Label className="text-sm font-medium text-gray-700">Descrição</Label>
                                <Textarea
                                    placeholder="Descreva o propósito deste template..."
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={2}
                                    className="border-2 border-gray-200 rounded-xl focus:border-blue-500 resize-none"
                                />
                            </div>
                        </div>
                    )}

                    {/* Step 2: Escalonamento */}
                    {currentStep === 2 && (
                        <div className="space-y-5">
                            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                                <div className="flex items-start gap-3">
                                    <Info className="h-5 w-5 text-blue-500 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-blue-900">Como funciona o escalonamento?</p>
                                        <p className="text-sm text-blue-700 mt-1">
                                            Quando a diferença do PMR em relação à meta for ≥ X%, o preço será ajustado em ±Y%.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <div className="p-1.5 bg-amber-100 rounded-md">
                                        <TrendingUp className="h-4 w-4 text-amber-600" />
                                    </div>
                                    Thresholds de Escalonamento
                                </Label>
                                <Button type="button" variant="outline" size="sm" onClick={addThreshold} className="h-9">
                                    <Plus className="h-4 w-4 mr-1" />
                                    Adicionar
                                </Button>
                            </div>

                            <div className="space-y-3">
                                {formData.scale_thresholds.map((threshold, index) => (
                                    <div key={index} className={`p-4 rounded-xl border-2 bg-white ${
                                        index === 0 ? 'border-emerald-200' :
                                            index === 1 ? 'border-blue-200' :
                                                index === 2 ? 'border-amber-200' :
                                                    'border-red-200'
                                    }`}>
                                        <div className="flex items-center gap-4">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                                                index === 0 ? 'bg-emerald-100 text-emerald-700' :
                                                    index === 1 ? 'bg-blue-100 text-blue-700' :
                                                        index === 2 ? 'bg-amber-100 text-amber-700' :
                                                            'bg-red-100 text-red-700'
                                            }`}>
                                                {index + 1}
                                            </div>

                                            <div className="flex items-center gap-2 flex-1">
                                                <span className="text-sm text-gray-500">Se diferença ≥</span>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    className="w-20 h-10 text-center font-medium border-2 rounded-lg"
                                                    value={threshold.difference_percentage}
                                                    onChange={(e) => updateThreshold(index, 'difference_percentage', Number(e.target.value))}
                                                />
                                                <span className="text-sm text-gray-500">%</span>
                                            </div>

                                            <ArrowRight className="h-4 w-4 text-gray-300" />

                                            <div className="flex items-center gap-2 flex-1">
                                                <span className="text-sm text-gray-500">ajustar</span>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    className="w-20 h-10 text-center font-medium border-2 rounded-lg"
                                                    value={threshold.scale_percentage}
                                                    onChange={(e) => updateThreshold(index, 'scale_percentage', Number(e.target.value))}
                                                />
                                                <span className="text-sm text-gray-500">%</span>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => removeThreshold(index)}
                                                disabled={formData.scale_thresholds.length <= 1}
                                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 3: Proteções (Guards) */}
                    {currentStep === 3 && (
                        <div className="space-y-5">
                            <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                                <div className="flex items-start gap-3">
                                    <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-amber-900">Condições de Proteção</p>
                                        <p className="text-sm text-amber-700 mt-1">
                                            Guards impedem que o sync execute se as condições não forem atendidas.
                                            Ex: Só sincroniza se impressões ≥ 300.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <div className="p-1.5 bg-blue-100 rounded-md">
                                        <Shield className="h-4 w-4 text-blue-600" />
                                    </div>
                                    Condições de Guarda
                                    <span className="text-xs text-gray-400 font-normal">(opcional)</span>
                                </Label>
                                <Button type="button" variant="outline" size="sm" onClick={addGuard} className="h-9">
                                    <Plus className="h-4 w-4 mr-1" />
                                    Adicionar
                                </Button>
                            </div>

                            {formData.guard_conditions.length === 0 ? (
                                <div className="p-8 border-2 border-dashed border-gray-200 rounded-xl text-center">
                                    <Shield className="h-8 w-8 text-gray-300 mx-auto" />
                                    <p className="text-sm text-gray-500 mt-2">Nenhuma condição configurada</p>
                                    <p className="text-xs text-gray-400 mt-1">O sync executará sem restrições</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {formData.guard_conditions.map((guard, index) => (
                                        <div key={index} className="p-4 rounded-xl border-2 border-gray-200 bg-white">
                                            <div className="flex items-center gap-3">
                                                {/* Metric Select */}
                                                <div className="relative flex-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => { closeAllDropdowns(); setMetricOpenIndex(metricOpenIndex === index ? null : index); }}
                                                        className="w-full h-10 px-3 text-left text-sm font-medium border-2 border-gray-200 rounded-lg bg-white flex items-center justify-between hover:border-gray-300"
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            {METRICS_OPTIONS.find(m => m.value === guard.metric)?.icon}
                                                            {METRICS_OPTIONS.find(m => m.value === guard.metric)?.label}
                                                        </span>
                                                        <ChevronDown className="h-4 w-4 text-gray-400" />
                                                    </button>
                                                    {metricOpenIndex === index && (
                                                        <>
                                                            <div className="fixed inset-0 z-10" onClick={() => setMetricOpenIndex(null)} />
                                                            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                                                {METRICS_OPTIONS.map((m) => (
                                                                    <button
                                                                        key={m.value}
                                                                        type="button"
                                                                        onClick={() => { updateGuard(index, 'metric', m.value); setMetricOpenIndex(null); }}
                                                                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${guard.metric === m.value ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
                                                                    >
                                                                        <span className="flex items-center gap-2">{m.icon} {m.label}</span>
                                                                        {guard.metric === m.value && <Check className="h-4 w-4" />}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>

                                                {/* Operator Select */}
                                                <div className="relative w-44">
                                                    <button
                                                        type="button"
                                                        onClick={() => { closeAllDropdowns(); setOperatorOpenIndex(operatorOpenIndex === index ? null : index); }}
                                                        className="w-full h-10 px-3 text-left text-sm font-medium border-2 border-gray-200 rounded-lg bg-white flex items-center justify-between hover:border-gray-300"
                                                    >
                                                        {OPERATORS.find(o => o.value === guard.operator)?.label}
                                                        <ChevronDown className="h-4 w-4 text-gray-400" />
                                                    </button>
                                                    {operatorOpenIndex === index && (
                                                        <>
                                                            <div className="fixed inset-0 z-10" onClick={() => setOperatorOpenIndex(null)} />
                                                            <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                                                {OPERATORS.map((o) => (
                                                                    <button
                                                                        key={o.value}
                                                                        type="button"
                                                                        onClick={() => { updateGuard(index, 'operator', o.value); setOperatorOpenIndex(null); }}
                                                                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${guard.operator === o.value ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
                                                                    >
                                                                        {o.label}
                                                                        {guard.operator === o.value && <Check className="h-4 w-4" />}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>

                                                {/* Value Input */}
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    className="w-28 h-10 text-center font-medium border-2 rounded-lg"
                                                    value={guard.value}
                                                    onChange={(e) => updateGuard(index, 'value', Number(e.target.value))}
                                                />

                                                <button
                                                    type="button"
                                                    onClick={() => removeGuard(index)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Resumo Final */}
                            <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 mt-6">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Resumo do Template</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                                        <p className="text-xs text-gray-500">Nome</p>
                                        <p className="text-sm font-semibold text-gray-900">{formData.name || '-'}</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                                        <p className="text-xs text-gray-500">Modo</p>
                                        <p className="text-sm font-semibold text-gray-900">{formData.sync_mode === 'automatic' ? 'Automático' : 'Manual'}</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                                        <p className="text-xs text-gray-500">Thresholds</p>
                                        <p className="text-sm font-semibold text-gray-900">{formData.scale_thresholds.length} configurados</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                                        <p className="text-xs text-gray-500">Guards</p>
                                        <p className="text-sm font-semibold text-gray-900">{formData.guard_conditions.length} condições</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : onOpenChange(false)}
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        {currentStep > 1 ? 'Voltar' : 'Cancelar'}
                    </Button>

                    {currentStep < 3 ? (
                        <Button
                            type="button"
                            onClick={() => setCurrentStep(currentStep + 1)}
                            disabled={!canProceed()}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            Próximo
                            <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            onClick={() => createMutation.mutate()}
                            disabled={!formData.name.trim() || createMutation.isPending}
                            className="bg-emerald-600 hover:bg-emerald-700"
                        >
                            {createMutation.isPending ? 'Criando...' : 'Criar Template'}
                            <Check className="h-4 w-4 ml-2" />
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}